import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';

import { PAGAMENTO_MAX_ERROS_CONSECUTIVOS } from '@/shared/constants/pagamentoPolling';

import { MENSAGEM_ACEITE_OBRIGATORIO } from '../constants';
import type {
  CampoParametroSelf,
  ChatMensagemSelf,
  ObjetivoOpcao,
  ObjetivoSelf,
  PagamentoSelfStatus,
  PerfilSelf,
  PlanoPeriodo,
  PlanoTier,
  SelfStep,
  SessaoSelf,
  ValoresParametros,
} from '../types';
import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';
import {
  buscarContextoEmpresaSelf,
  carregarCamposObjetivoSelf,
  carregarObjetivosSelf,
  consultarCnpjNegociacaoSelf,
  consultarPagamentoSelf,
  criarAgenteSelf,
  enviarMensagemSimulacaoSelf,
  iniciarPagamentoSelf,
} from './actions';

interface NegociacaoSelfState {
  step: SelfStep;
  objetivo: ObjetivoSelf | null;
  perfil: PerfilSelf;
  leadId: string | null;
  accessToken: string | null;
  objetivosCatalogo: ObjetivoOpcao[];
  carregandoObjetivos: boolean;
  objetivosError: string | null;
  buscandoContexto: boolean;
  buscouContexto: boolean;
  contextoEncontrado: boolean;
  tentouBuscar: boolean;
  contextoError: string | null;
  consultandoCnpj: boolean;
  cnpjConsultado: string | null;
  cnpjEncontrado: boolean | null;
  cnpjError: string | null;
  razaoSocialAuto: boolean;
  nomeFantasiaAuto: boolean;
  camposObjetivo: CampoParametroSelf[];
  camposObjetivoDe: ObjetivoSelf | null;
  carregandoCampos: boolean;
  camposError: string | null;
  valoresParametros: ValoresParametros;
  criandoAgente: boolean;
  criarAgenteError: string | null;
  planoPeriodo: PlanoPeriodo;
  planoTier: PlanoTier;
  termosAceitos: boolean;
  restauracaoSessaoConcluida: boolean;
  chatMensagens: ChatMensagemSelf[];
  chatInput: string;
  lexiDigitando: boolean;
  pagamentoError: string | null;
  invoiceUrl: string | null;
  pagamentoStatus: PagamentoSelfStatus | null;
  iniciandoPagamento: boolean;
  consultandoPagamento: boolean;
  pagamentoErrosConsecutivos: number;
  pollingPagamentoExpirado: boolean;
}

const initialState: NegociacaoSelfState = {
  step: 'landing',
  objetivo: null,
  perfil: { nome: '', email: '', cnpj: '', razaoSocial: '', nomeFantasia: '', site: '', sobre: '' },
  leadId: null,
  accessToken: null,
  objetivosCatalogo: [],
  carregandoObjetivos: false,
  objetivosError: null,
  buscandoContexto: false,
  buscouContexto: false,
  contextoEncontrado: false,
  tentouBuscar: false,
  contextoError: null,
  consultandoCnpj: false,
  cnpjConsultado: null,
  cnpjEncontrado: null,
  cnpjError: null,
  razaoSocialAuto: false,
  nomeFantasiaAuto: false,
  camposObjetivo: [],
  camposObjetivoDe: null,
  carregandoCampos: false,
  camposError: null,
  valoresParametros: {},
  criandoAgente: false,
  criarAgenteError: null,
  planoPeriodo: 'anual',
  planoTier: 'intermediario',
  termosAceitos: false,
  restauracaoSessaoConcluida: false,
  chatMensagens: [],
  chatInput: '',
  lexiDigitando: false,
  pagamentoError: null,
  invoiceUrl: null,
  pagamentoStatus: null,
  iniciandoPagamento: false,
  consultandoPagamento: false,
  pagamentoErrosConsecutivos: 0,
  pollingPagamentoExpirado: false,
};

const valoresIniciais = (campos: CampoParametroSelf[]): ValoresParametros =>
  Object.fromEntries(campos.map((campo) => [campo.key, campo.tipo === 'BOOLEAN' ? false : '']));

const invalidarConsultaCnpj = (state: NegociacaoSelfState): void => {
  state.cnpjConsultado = null;
  state.cnpjEncontrado = null;
  state.cnpjError = null;
  if (state.razaoSocialAuto) {
    state.perfil.razaoSocial = '';
  }
  if (state.nomeFantasiaAuto) {
    state.perfil.nomeFantasia = '';
  }
};

const invalidarEstadoDerivadoDoPerfil = (
  state: NegociacaoSelfState,
  campo: keyof PerfilSelf,
): void => {
  if (campo === 'razaoSocial') {
    state.razaoSocialAuto = false;
  }
  if (campo === 'nomeFantasia') {
    state.nomeFantasiaAuto = false;
  }
  if (campo === 'cnpj') {
    invalidarConsultaCnpj(state);
  }
};

const aplicarStatusPagamento = (state: NegociacaoSelfState, status: PagamentoSelfStatus): void => {
  state.pagamentoStatus = status;
  if (status === 'PAGO') {
    state.step = 'success';
    return;
  }
  if (status === 'CANCELADO' || status === 'ERRO') {
    state.step = 'errorPayment';
    state.pagamentoError = 'O pagamento foi cancelado ou não pôde ser concluído.';
  }
};

const negociacaoSelfSlice = createSlice({
  name: 'negociacaoSelf',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<SelfStep>) => {
      state.step = action.payload;
    },
    setObjetivo: (state, action: PayloadAction<ObjetivoSelf>) => {
      state.objetivo = action.payload;
    },
    setPerfilCampo: (state, action: PayloadAction<{ campo: keyof PerfilSelf; valor: string }>) => {
      const { campo, valor } = action.payload;
      const cnpjInalterado =
        campo === 'cnpj' && normalizeCpfCnpj(state.perfil.cnpj) === normalizeCpfCnpj(valor);
      state.perfil[campo] = valor;
      if (cnpjInalterado) return;
      invalidarEstadoDerivadoDoPerfil(state, campo);
    },
    marcarTentouBuscar: (state) => {
      state.tentouBuscar = true;
    },
    editarDadosPerfil: (state) => {
      state.buscandoContexto = false;
      state.buscouContexto = false;
      state.contextoEncontrado = false;
      state.tentouBuscar = false;
      state.contextoError = null;
    },
    setValorParametro: (state, action: PayloadAction<{ key: string; valor: string | boolean }>) => {
      state.valoresParametros[action.payload.key] = action.payload.valor;
    },
    setPlanoPeriodo: (state, action: PayloadAction<PlanoPeriodo>) => {
      state.planoPeriodo = action.payload;
    },
    setPlanoTier: (state, action: PayloadAction<PlanoTier>) => {
      state.planoTier = action.payload;
    },
    setTermosAceitos: (state, action: PayloadAction<boolean>) => {
      state.termosAceitos = action.payload;
    },
    restaurarSessao: (state, action: PayloadAction<SessaoSelf>) => {
      state.restauracaoSessaoConcluida = true;
      state.leadId = action.payload.leadId;
      state.accessToken = action.payload.accessToken;
      state.objetivo = action.payload.objetivo;
      if (urlPagamentoSegura(action.payload.invoiceUrl)) {
        state.invoiceUrl = action.payload.invoiceUrl;
      }
      if (action.payload.planoTier) state.planoTier = action.payload.planoTier;
      if (action.payload.planoPeriodo) state.planoPeriodo = action.payload.planoPeriodo;
      if (action.payload.termosAceitos) state.termosAceitos = true;
    },
    dispensarRestauracaoSessao: (state) => {
      state.restauracaoSessaoConcluida = true;
    },
    setChatInput: (state, action: PayloadAction<string>) => {
      state.chatInput = action.payload;
    },
    adicionarMensagemUsuario: (state, action: PayloadAction<string>) => {
      state.chatMensagens.push({
        id: `user-${state.chatMensagens.length}`,
        autor: 'user',
        texto: action.payload,
      });
      state.chatInput = '';
    },
    marcarPollingPagamentoExpirado: (state) => {
      state.pollingPagamentoExpirado = true;
    },
    reiniciar: () => ({ ...initialState, restauracaoSessaoConcluida: true }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(carregarObjetivosSelf.pending, (state) => {
        state.carregandoObjetivos = true;
        state.objetivosError = null;
      })
      .addCase(carregarObjetivosSelf.fulfilled, (state, action) => {
        state.carregandoObjetivos = false;
        state.objetivosCatalogo = action.payload;
      })
      .addCase(carregarObjetivosSelf.rejected, (state, action) => {
        state.carregandoObjetivos = false;
        state.objetivosError = action.payload ?? 'Erro ao carregar os objetivos';
      })
      .addCase(consultarCnpjNegociacaoSelf.pending, (state, action) => {
        state.consultandoCnpj = true;
        state.cnpjConsultado = action.meta.arg;
        state.cnpjEncontrado = null;
        state.cnpjError = null;
      })
      .addCase(consultarCnpjNegociacaoSelf.fulfilled, (state, action) => {
        state.consultandoCnpj = false;
        if (action.meta.arg !== normalizeCpfCnpj(state.perfil.cnpj)) return;
        state.cnpjEncontrado = action.payload.encontrado;
        if (!action.payload.encontrado) return;

        if (!state.perfil.razaoSocial.trim() || state.razaoSocialAuto) {
          state.perfil.razaoSocial = action.payload.razaoSocial;
          state.razaoSocialAuto = true;
        }
        if (!state.perfil.nomeFantasia.trim() || state.nomeFantasiaAuto) {
          state.perfil.nomeFantasia = action.payload.nomeFantasia;
          state.nomeFantasiaAuto = true;
        }
      })
      .addCase(consultarCnpjNegociacaoSelf.rejected, (state, action) => {
        state.consultandoCnpj = false;
        if (action.meta.aborted) return;
        if (action.meta.arg !== normalizeCpfCnpj(state.perfil.cnpj)) return;
        state.cnpjError = action.payload ?? 'Não conseguimos consultar o CNPJ agora';
      })
      .addCase(buscarContextoEmpresaSelf.pending, (state) => {
        state.buscandoContexto = true;
        state.contextoError = null;
      })
      .addCase(buscarContextoEmpresaSelf.fulfilled, (state, action) => {
        state.buscandoContexto = false;
        state.buscouContexto = true;
        if (state.leadId !== action.payload.leadId) {
          state.termosAceitos = false;
        }
        state.leadId = action.payload.leadId;
        state.accessToken = action.payload.accessToken;
        state.contextoEncontrado = action.payload.encontrado;
        if (action.payload.encontrado && action.payload.sobre) {
          state.perfil.sobre = action.payload.sobre;
        }
      })
      .addCase(buscarContextoEmpresaSelf.rejected, (state, action) => {
        state.buscandoContexto = false;
        state.contextoError = action.payload ?? 'Erro ao buscar contexto da empresa';
      })
      .addCase(carregarCamposObjetivoSelf.pending, (state) => {
        state.carregandoCampos = true;
        state.camposError = null;
      })
      .addCase(carregarCamposObjetivoSelf.fulfilled, (state, action) => {
        state.carregandoCampos = false;
        state.camposObjetivo = action.payload.campos;
        if (state.camposObjetivoDe !== action.payload.objetivo) {
          state.valoresParametros = valoresIniciais(action.payload.campos);
        }
        state.camposObjetivoDe = action.payload.objetivo;
      })
      .addCase(carregarCamposObjetivoSelf.rejected, (state, action) => {
        state.carregandoCampos = false;
        state.camposError = action.payload ?? 'Erro ao carregar os parâmetros do objetivo';
      })
      .addCase(criarAgenteSelf.pending, (state) => {
        state.criandoAgente = true;
        state.criarAgenteError = null;
      })
      .addCase(criarAgenteSelf.fulfilled, (state, action) => {
        state.criandoAgente = false;
        state.chatMensagens = [
          { id: 'lexi-0', autor: 'lexi', texto: action.payload.mensagemInicial },
        ];
        state.step = 'revisao';
      })
      .addCase(criarAgenteSelf.rejected, (state, action) => {
        state.criandoAgente = false;
        state.criarAgenteError = action.meta.aborted
          ? null
          : (action.payload ?? 'Erro ao criar o agente');
      })
      .addCase(enviarMensagemSimulacaoSelf.pending, (state) => {
        state.lexiDigitando = true;
      })
      .addCase(enviarMensagemSimulacaoSelf.fulfilled, (state, action) => {
        state.lexiDigitando = false;
        state.chatMensagens.push({
          id: `lexi-${state.chatMensagens.length}`,
          autor: 'lexi',
          texto: action.payload,
        });
      })
      .addCase(enviarMensagemSimulacaoSelf.rejected, (state) => {
        state.lexiDigitando = false;
      })
      .addCase(iniciarPagamentoSelf.pending, (state) => {
        state.iniciandoPagamento = true;
        state.pagamentoError = null;
        state.pagamentoErrosConsecutivos = 0;
        state.pollingPagamentoExpirado = false;
      })
      .addCase(iniciarPagamentoSelf.fulfilled, (state, action) => {
        state.iniciandoPagamento = false;
        state.invoiceUrl = action.payload.invoiceUrl;
        aplicarStatusPagamento(state, action.payload.status);
        if (state.step === 'success' || state.step === 'errorPayment') return;

        if (!urlPagamentoSegura(action.payload.invoiceUrl)) {
          state.step = 'errorPayment';
          state.pagamentoError =
            'Não recebemos um link de pagamento válido. Tente novamente ou fale com o suporte.';
          return;
        }
        state.step = 'waitingPayment';
      })
      .addCase(iniciarPagamentoSelf.rejected, (state, action) => {
        state.iniciandoPagamento = false;
        state.pagamentoError = action.payload ?? 'Erro ao iniciar o pagamento';
        if (action.payload === MENSAGEM_ACEITE_OBRIGATORIO) return;
        state.step = 'errorPayment';
      })
      .addCase(consultarPagamentoSelf.pending, (state) => {
        state.consultandoPagamento = true;
        state.pollingPagamentoExpirado = false;
      })
      .addCase(consultarPagamentoSelf.fulfilled, (state, action) => {
        state.consultandoPagamento = false;
        state.pagamentoErrosConsecutivos = 0;
        aplicarStatusPagamento(state, action.payload.status);
      })
      .addCase(consultarPagamentoSelf.rejected, (state, action) => {
        state.consultandoPagamento = false;
        if (action.meta.aborted) return;
        state.pagamentoErrosConsecutivos += 1;
        if (state.pagamentoErrosConsecutivos >= PAGAMENTO_MAX_ERROS_CONSECUTIVOS) {
          state.step = 'errorPayment';
          state.pagamentoError =
            action.payload ?? 'Não conseguimos confirmar seu pagamento. Tente novamente.';
        }
      });
  },
});

export const {
  setStep,
  setObjetivo,
  setPerfilCampo,
  marcarTentouBuscar,
  editarDadosPerfil,
  setValorParametro,
  setPlanoPeriodo,
  setPlanoTier,
  setTermosAceitos,
  restaurarSessao,
  dispensarRestauracaoSessao,
  setChatInput,
  adicionarMensagemUsuario,
  marcarPollingPagamentoExpirado,
  reiniciar,
} = negociacaoSelfSlice.actions;

export default negociacaoSelfSlice.reducer;
